import { useEffect, useMemo, useState } from "react"
import {
    deleteRestaurantMenuTranslation,
    IRestaurantMenuItemTranslation,
    IRestaurantMenuLanguage,
    setRestaurantMenuTranslation,
} from "@/shared/api/restaurant-menus"
import notify from "@/shared/lib/notify"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { Modal } from "@/shared/ui/modal"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import {
    RestaurantMenuEntityTarget,
    RestaurantMenuTranslationDraft,
} from "../model/types"

interface MenuTranslationsModalProps {
    isOpen: boolean
    menuId: string
    target: RestaurantMenuEntityTarget | null
    languages: IRestaurantMenuLanguage[]
    translations: IRestaurantMenuItemTranslation[] | null
    onClose: () => void
    onChanged: () => Promise<void>
}

const getLanguageLabel = (language: IRestaurantMenuLanguage): string => {
    return language.name ? `${language.name} (${language.code})` : language.code ?? "—"
}

export const MenuTranslationsModal = ({
    isOpen,
    menuId,
    target,
    languages,
    translations,
    onClose,
    onChanged,
}: MenuTranslationsModalProps) => {
    const [drafts, setDrafts] = useState<Record<string, RestaurantMenuTranslationDraft>>({})
    const [pendingLanguage, setPendingLanguage] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const [languageToDelete, setLanguageToDelete] = useState<string | null>(null)

    const availableLanguages = useMemo(() => {
        return languages.filter((language) => Boolean(language.code))
    }, [languages])

    const existingTranslations = useMemo(() => {
        if (!target) {
            return []
        }

        const overviewTranslations = translations?.filter((translation) => {
            return translation.item_type === target.entityType && translation.item_id === target.entityId
        }) ?? []

        if (overviewTranslations.length > 0) {
            return overviewTranslations
        }

        return target.translations?.map((translation) => ({
            item_type: target.entityType,
            item_id: target.entityId,
            language_code: translation.language_code,
            name: translation.name,
            description: translation.description,
        })) ?? []
    }, [target, translations])

    useEffect(() => {
        if (!isOpen || !target) {
            setDrafts({})
            setFormError(null)
            setPendingLanguage(null)
            setLanguageToDelete(null)
            return
        }

        const nextDrafts = availableLanguages.reduce<Record<string, RestaurantMenuTranslationDraft>>((acc, language) => {
            const languageCode = language.code ?? ""
            const existing = existingTranslations.find((translation) => translation.language_code === languageCode)

            acc[languageCode] = {
                language_code: languageCode,
                name: existing?.name ?? "",
                description: existing?.description ?? "",
            }

            return acc
        }, {})

        setDrafts(nextDrafts)
        setFormError(null)
    }, [availableLanguages, existingTranslations, isOpen, target])

    const updateDraft = (languageCode: string, field: "name" | "description", value: string) => {
        setDrafts((state) => ({
            ...state,
            [languageCode]: {
                ...state[languageCode],
                language_code: languageCode,
                [field]: value,
            },
        }))
    }

    const hasExistingTranslation = (languageCode: string): boolean => {
        return existingTranslations.some((translation) => translation.language_code === languageCode)
    }

    const handleSave = async (languageCode: string) => {
        if (!target) {
            return
        }

        const draft = drafts[languageCode]
        if (!draft) {
            return
        }

        try {
            setPendingLanguage(languageCode)
            setFormError(null)
            await setRestaurantMenuTranslation(menuId, target.entityType, target.entityId, {
                language_code: languageCode,
                name: draft.name.trim(),
                description: target.entityType === "category" ? "" : draft.description.trim(),
            })
            notify.success("Перевод сохранён")
            await onChanged()
        } catch (error) {
            setFormError(getApiErrorMessage(error))
        } finally {
            setPendingLanguage(null)
        }
    }

    const handleConfirmDelete = async () => {
        if (!target || !languageToDelete) {
            return
        }

        try {
            setPendingLanguage(languageToDelete)
            setFormError(null)
            await deleteRestaurantMenuTranslation(menuId, target.entityType, target.entityId, languageToDelete)
            notify.success("Перевод удалён")
            setLanguageToDelete(null)
            await onChanged()
        } catch (error) {
            setFormError(getApiErrorMessage(error))
        } finally {
            setPendingLanguage(null)
        }
    }

    return (
        <>
            <Modal isOpen={isOpen} title={`Переводы: ${target?.title ?? ""}`} onRequestClose={onClose}>
                <div className="flex min-w-[760px] flex-col gap-4">
                    {availableLanguages.length === 0 && (
                        <div className="info_panel">Нет языков для редактирования переводов</div>
                    )}

                    {availableLanguages.length > 0 && (
                        <div className="overflow-auto">
                            <table className="react-table">
                                <thead>
                                    <tr>
                                        <th>Язык</th>
                                        <th>Название</th>
                                        {target?.entityType !== "category" && <th>Описание</th>}
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableLanguages.map((language) => {
                                        const languageCode = language.code ?? ""
                                        const draft = drafts[languageCode] ?? {
                                            language_code: languageCode,
                                            name: "",
                                            description: "",
                                        }

                                        return (
                                            <tr key={languageCode}>
                                                <td>{getLanguageLabel(language)}</td>
                                                <td>
                                                    <input
                                                        value={draft.name}
                                                        onChange={(event) => updateDraft(languageCode, "name", event.target.value)}
                                                        className="input_field"
                                                        placeholder="Название"
                                                    />
                                                </td>
                                                {target?.entityType !== "category" && (
                                                    <td>
                                                        <textarea
                                                            value={draft.description}
                                                            onChange={(event) => updateDraft(languageCode, "description", event.target.value)}
                                                            className="input_field min-h-[80px] resize-none"
                                                            placeholder="Описание"
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="flex flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            disabled={pendingLanguage === languageCode}
                                                            onClick={() => handleSave(languageCode)}
                                                            className="form_handle_button !h-9 !w-auto px-4 disabled:opacity-50"
                                                        >
                                                            Сохранить
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={!hasExistingTranslation(languageCode) || pendingLanguage === languageCode}
                                                            onClick={() => setLanguageToDelete(languageCode)}
                                                            className="form_cancel_button !h-9 !w-auto px-4 disabled:opacity-50"
                                                        >
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {formError && <div className="text-color-danger">{formError}</div>}
                </div>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(languageToDelete)}
                title="Удалить перевод"
                message="Перевод для выбранного языка будет удалён. Продолжить?"
                confirmText="Удалить"
                onConfirm={handleConfirmDelete}
                onCancel={() => setLanguageToDelete(null)}
            />
        </>
    )
}
